import React, { useState, useEffect, useMemo } from 'react';
import { FilterBar } from '@/components/FilterBar';
import { ResultsGrid } from '@/components/ResultsGrid';
import { TrendingSection } from '@/components/TrendingSection';
import { SmartSearchSuggestions } from '@/components/SmartSearchSuggestions';
import { NewsletterSignup } from '@/components/NewsletterSignup';
import { FloatingActions } from '@/components/FloatingActions';
import { UserMenu } from '@/components/UserMenu';
import { ComparisonBadge } from '@/components/ComparisonBadge';
import { SmartSortIndicator } from '@/components/SmartSortIndicator';
import { ThemeToggle } from '@/components/ThemeToggle';
import { AnimatedCounter } from '@/components/AnimatedCounter';
import { CommandPalette } from '@/components/CommandPalette';
import { UseCaseNavigation } from '@/components/UseCaseNavigation';
import { HowItWorks } from '@/components/HowItWorks';
import { SocialProof } from '@/components/SocialProof';
import { WhyVergo } from '@/components/WhyVergo';
import { ComparisonTable } from '@/components/ComparisonTable';
import { BottomNav } from '@/components/BottomNav';
import { SEOHead } from '@/components/SEOHead';
import { useSearch } from '@/hooks/useSearch';
import { useSmartSort } from '@/hooks/useSmartSort';
import { useFavorites } from '@/hooks/useFavorites';
import { useComparisonContext } from '@/contexts/ComparisonContext';
import { AISearchToggle } from '@/components/AISearchToggle';
import { GlobalSearchBar } from '@/components/search/GlobalSearchBar';
import { Heart, Tag, Bot, Zap, Sparkles, ArrowRight, GitCompareArrows, Calendar } from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Bot as BotType } from '@/types/Bot';
import { Button } from '@/components/ui/button';
import { VergoLogo } from '@/components/VergoLogo';

const Index = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { searchTerm, setSearchTerm, filters, setFilters, filteredBots } = useSearch();
  const { toggleFavorite, isFavorite, favorites } = useFavorites();
  const { 
    comparisonList, addToComparison, removeFromComparison, clearComparison, isInComparison, canAddMore 
  } = useComparisonContext();
  
  const [showFavorites, setShowFavorites] = useState(false);
  const [, setLastLocalSortOrder] = useState<BotType[]>([]);
  const [activeUseCase, setActiveUseCase] = useState('all');

  const handleCommandSearch = (query: string) => setSearchTerm(query);
  const handleBotClick = (bot: BotType) => navigate(`/bot/${bot.id}`);

  const intent = searchTerm.trim();
  const isAiSortEnabled = filters.sortBy === 'ai';
  
  const { mergedResults, isLoading: isAiSortLoading, scores, scoringNotes, usedAiFallback, sessionDisabled, replayLastRequest } = useSmartSort({
    intent, filters, visibleItems: filteredBots, enabled: isAiSortEnabled
  });

  const displayBotsBeforeFavorites = useMemo(() => {
    let bots = isAiSortEnabled ? mergedResults : filteredBots;
    if (activeUseCase !== 'all') {
      const useCaseMap: Record<string, string[]> = {
        'productivity': ['Business & Productivity', 'Utilities & Tools', 'Presentations & Documents'],
        'creative': ['Design & Creative Tools', 'Image Editing & Generation', 'Avatar & Character'],
        'coding': ['Developer Tools'],
        'learning': ['Education & Learning'],
        'video': ['Video Creation & Editing'],
        'audio': ['Audio & Music'],
        'chat': ['AI Chat & Assistants'],
        'design': ['Home & Interior Design', 'Website Builders'],
        'writing': ['Content & Writing'],
      };
      const categories = useCaseMap[activeUseCase] || [];
      if (categories.length > 0) {
        bots = bots.filter(bot => 
          categories.some(cat => 
            bot.category?.toLowerCase().includes(cat.toLowerCase()) ||
            bot.useCase?.toLowerCase().includes(cat.toLowerCase()) ||
            (bot as any).use_case?.toLowerCase().includes(cat.toLowerCase())
          )
        );
      }
    }
    return bots;
  }, [isAiSortEnabled, mergedResults, filteredBots, activeUseCase]);

  const favoriteBots = displayBotsBeforeFavorites.filter(bot => isFavorite(bot.id));
  const displayBots = showFavorites ? favoriteBots : displayBotsBeforeFavorites;
  
  useEffect(() => {
    if (!isAiSortEnabled) setLastLocalSortOrder(filteredBots);
  }, [isAiSortEnabled, filteredBots]);

  useEffect(() => {
    if (isAiSortEnabled) {
      searchParams.set('sortBy', 'ai');
      searchParams.set('aiVersion', '1');
    } else {
      searchParams.delete('sortBy');
      searchParams.delete('aiVersion');
    }
    setSearchParams(searchParams, { replace: true });
  }, [isAiSortEnabled, searchParams, setSearchParams]);

  useEffect(() => {
    const urlSortBy = searchParams.get('sortBy');
    const urlAiVersion = searchParams.get('aiVersion');
    if (urlSortBy === 'ai' && urlAiVersion === '1' && filters.sortBy !== 'ai') {
      setFilters(prev => ({ ...prev, sortBy: 'ai' }));
    }
  }, []);

  const handleSortChange = (newFilters: typeof filters) => setFilters(newFilters);

  const scrollToTools = () => {
    document.getElementById('tools-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-background pb-16 md:pb-0">
      <SEOHead
        title="Vergo | The Ultimate AI Chatbot Hub & Search Engine"
        description="Discover, compare, and use the best AI tools in one place. Vergo is your all-in-one AI hub for productivity, research, and automation."
        keywords="AI chatbot, AI assistant, chatbot comparison, AI tools, best AI tools 2026, AI hub, AI directory"
      />

      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur-sm">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <VergoLogo size="sm" />
              <span className="font-semibold text-lg hidden sm:inline">Vergo</span>
            </Link>
            <div className="flex items-center gap-2">
              <CommandPalette onSearch={handleCommandSearch} />
              <Link
                to="/deals"
                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                <Tag className="w-4 h-4" />
                Deals
              </Link>
              <button
                onClick={() => setShowFavorites(!showFavorites)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors ${
                  showFavorites ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                <Heart className={`w-4 h-4 ${showFavorites ? 'fill-current' : ''}`} />
                <span className="hidden sm:inline">{showFavorites ? `Favorites (${favorites.length})` : 'Favorites'}</span>
              </button>
              <ThemeToggle />
              <UserMenu />
            </div>
          </div>
        </div>
      </header>

      <main>
        {/* Hero Section */}
        <section className="relative py-20 md:py-32 px-4 overflow-hidden">
          {/* Decorative background logo */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 flex items-center justify-center"
          >
            <VergoLogo
              size="xl"
              className="w-[420px] h-[420px] md:w-[640px] md:h-[640px] opacity-[0.04] dark:opacity-[0.06] blur-[1px]"
            />
          </div>
          <div className="relative container mx-auto text-center max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-border text-xs text-muted-foreground mb-6">
              <Calendar className="w-3 h-3" />
              Last Updated: April 2026
            </div>

            <h1 className="text-4xl md:text-6xl font-bold tracking-tight leading-tight mb-6">
              Find, Compare, and Use the Best AI Tools in One Place
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10">
              Your all-in-one AI hub to save time, boost productivity, and replace dozens of tools.
            </p>

            <div className="max-w-2xl mx-auto mb-6">
              <GlobalSearchBar placeholder="Search AI tools... (e.g., coding assistant, image generator)" />
              <div className="flex justify-center mt-3">
                <AISearchToggle />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-10">
              <Button size="lg" onClick={scrollToTools}>
                Start Exploring <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate('/compare')}>
                <GitCompareArrows className="w-4 h-4 mr-1" /> Compare Tools
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate('/quiz')}>
                <Sparkles className="w-4 h-4 mr-1" /> Find My Tool
              </Button>
            </div>

            {/* Stats */}
            <div className="flex justify-center gap-10 md:gap-16">
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold">
                  <AnimatedCounter end={filteredBots.length || 150} suffix="+" />
                </div>
                <div className="text-sm text-muted-foreground flex items-center gap-1 justify-center mt-1">
                  <Bot className="w-3.5 h-3.5" /> AI Tools
                </div>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold">
                  <AnimatedCounter end={25} suffix="+" />
                </div>
                <div className="text-sm text-muted-foreground flex items-center gap-1 justify-center mt-1">
                  <Sparkles className="w-3.5 h-3.5" /> Categories
                </div>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold">
                  <AnimatedCounter end={10} suffix="K+" />
                </div>
                <div className="text-sm text-muted-foreground flex items-center gap-1 justify-center mt-1">
                  <Zap className="w-3.5 h-3.5" /> Users
                </div>
              </div>
            </div>

            {/* Category pills */}
            <div className="mt-8 flex flex-wrap justify-center gap-2">
              {['coding', 'writing', 'research', 'design', 'productivity'].map((category) => (
                <button
                  key={category}
                  onClick={() => setSearchTerm(category)}
                  className="px-4 py-2 rounded-full text-sm border border-border text-muted-foreground 
                            hover:border-foreground hover:text-foreground 
                            transition-colors capitalize"
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <HowItWorks />

        {/* Use-Case Navigation */}
        <UseCaseNavigation activeUseCase={activeUseCase} onUseCaseChange={setActiveUseCase} />

        {/* Trending Section */}
        {!showFavorites && !searchTerm && (
          <TrendingSection bots={filteredBots} onBotClick={handleBotClick} onToggleFavorite={toggleFavorite} isFavorite={isFavorite} />
        )}

        {/* Smart Search Suggestions */}
        {!showFavorites && (
          <section className="px-4 pb-6">
            <div className="container mx-auto">
              <SmartSearchSuggestions onSuggestionClick={setSearchTerm} currentQuery={searchTerm} />
            </div>
          </section>
        )}

        {/* Results Section */}
        <section id="tools-section" className="px-4 pb-16">
          <div className="container mx-auto space-y-6">
            <FilterBar filters={filters} onFiltersChange={handleSortChange} resultsCount={displayBots.length} isAiSortLoading={isAiSortLoading} />

            {isAiSortEnabled && (
              <div className="flex justify-center">
                <SmartSortIndicator scoringNotes={scoringNotes} scores={scores} usedAiFallback={usedAiFallback} sessionDisabled={sessionDisabled} onReplay={replayLastRequest} isDev={import.meta.env.DEV} />
              </div>
            )}
            
            {showFavorites && favorites.length === 0 && (
              <div className="text-center py-12">
                <Heart className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
                <h3 className="text-lg font-semibold mb-1">No favorites yet</h3>
                <p className="text-sm text-muted-foreground">Click the heart icon on any AI bot to save it here.</p>
              </div>
            )}
            
            <ResultsGrid 
              bots={displayBots} loading={false} onBotClick={handleBotClick}
              onToggleFavorite={toggleFavorite} isFavorite={isFavorite}
              isInComparison={isInComparison} canAddToComparison={canAddMore}
              onAddToComparison={addToComparison} onRemoveFromComparison={removeFromComparison}
            />
          </div>
        </section>

        {/* Comparison Table */}
        <ComparisonTable />

        {/* Social Proof */}
        <SocialProof />

        {/* Why Vergo */}
        <WhyVergo />

        {/* Newsletter */}
        <section className="px-4 py-16 border-t border-border">
          <div className="container mx-auto">
            <NewsletterSignup />
          </div>
        </section>

        <ComparisonBadge count={comparisonList.length} onClear={clearComparison} />
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-muted-foreground">
            <p>© {new Date().getFullYear()} Vergo. Find your perfect AI companion.</p>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
              <Link to="/about" className="hover:text-foreground transition-colors">About</Link>
              <Link to="/how-we-review" className="hover:text-foreground transition-colors">How We Review</Link>
              <Link to="/contact" className="hover:text-foreground transition-colors">Contact</Link>
              <Link to="/submit-bot" className="hover:text-foreground transition-colors">Submit a Tool</Link>
              <Link to="/terms" className="hover:text-foreground transition-colors">Terms</Link>
              <Link to="/privacy-policy" className="hover:text-foreground transition-colors">Privacy Policy</Link>
            </div>
          </div>
        </div>
      </footer>

      <FloatingActions />
      <BottomNav />
    </div>
  );
};

export default Index;
